import React, { useState } from "react";
import {
  useCreateAppCollectionMutation,
  useAddAppToCollectionMutation,
  useFetchAppCollectionsMutation,
} from "@/features/auth/api/authAPI";
import styles from "./CreateCollectionModal.module.scss";
import { XIcon } from "lucide-react";
import Loader from "@/shared/ui/Loader/Loader";

interface CreateCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  appId: number;
  onCollectionCreated?: () => void;
}

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  isOpen,
  onClose,
  appId,
  onCollectionCreated,
}) => {
  const [collectionName, setCollectionName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createAppCollection, { isLoading: isCreating }] =
    useCreateAppCollectionMutation();
  const [addAppToCollection] = useAddAppToCollectionMutation();
  const [fetchAppCollections] = useFetchAppCollectionsMutation();

  const handleCreateCollection = async () => {
    if (!collectionName.trim()) return;

    // Clear previous errors
    setError(null);

    try {
      // Create the collection
      await createAppCollection({ name: collectionName.trim() }).unwrap();

      // Fetch collections to get the newly created collection ID
      const collectionsResponse = await fetchAppCollections();
      const collections = collectionsResponse.data?.results || [];

      // Find the newly created collection (should be the one with the matching name)
      const newCollection = collections.find(
        (collection) => collection.name === collectionName.trim(),
      );

      if (newCollection) {
        // Add the app to the newly created collection
        await addAppToCollection({ id: newCollection.id, apps: [appId] });
      }

      // Call the callback to refresh collections if provided
      if (onCollectionCreated) {
        onCollectionCreated();
      }

      // Reset form and close modal
      setCollectionName("");
      setError(null);
      onClose();
    } catch (error: any) {
      console.error("Error creating collection:", error);

      // Handle API validation errors
      if (
        error?.data?.non_field_errors &&
        error.data.non_field_errors.length > 0
      ) {
        setError(error.data.non_field_errors[0]);
      } else if (error?.data?.name && error.data.name.length > 0) {
        setError(error.data.name[0]);
      } else if (error?.message) {
        setError(error.message);
      } else {
        setError("Failed to create collection. Please try again.");
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCreateCollection();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCollectionName(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  if (!isOpen) return null;

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={styles.modal} onClick={handleModalClick}>
      <div className={styles.header}>
        <h3>Create New Collection</h3>
        <button className={styles.closeButton} onClick={onClose}>
          <XIcon color="#BABABA" width={16} height={16} />
        </button>
      </div>
      <div className={styles.dividerContainer}>
        <div className={styles.divider} />
      </div>
      <div className={styles.content}>
        <div className={styles.inputContainer}>
          <input
            type="text"
            value={collectionName}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter collection name"
            className={`${styles.input} ${error ? styles.inputError : ""}`}
            autoFocus
            maxLength={35}
          />
          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={styles.createButton}
            onClick={handleCreateCollection}
            disabled={isCreating || !collectionName.trim()}
          >
            {isCreating ? (
              <Loader color="#fff" size={16} />
            ) : (
              <span>Create</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCollectionModal;
